/**
 * Paste-to-trigger helpers for AI import inputs.
 *
 * Several inputs across the app accept a URL and hand it to an AI parser: the
 * LinkedIn field in Add {{Candidate}}, the shared company lookup, the enrich
 * dialogs. Dropping a URL into one of them is an unambiguous "parse this" —
 * there is nothing else a recruiter means by pasting a LinkedIn profile — so the
 * AI should run on the paste instead of making the user press the button next to
 * the field they just filled.
 *
 * Typing the same characters does NOT trigger anything: a half-typed URL becomes
 * momentarily valid on its way to the real one, and firing a metered AI call per
 * keystroke-that-happens-to-parse is both wrong and expensive. Only a paste that
 * leaves the whole field holding a valid URL counts.
 */
import type { ClipboardEvent } from 'react';

/**
 * The value the input will hold once this paste is applied — the current value
 * with the selected range (or the caret position) replaced by the pasted text.
 *
 * Read during the paste event, before React's `onChange` runs, so callers can
 * act on the incoming value without waiting for a state round-trip.
 */
export function valueAfterPaste(event: ClipboardEvent<HTMLInputElement>): string {
  const input = event.currentTarget;
  const pasted = event.clipboardData.getData('text');
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  return input.value.slice(0, start) + pasted + input.value.slice(end);
}
